import { transparentize } from 'polished'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import styled from 'styled-components'
import {
  resizeToCover,
  scrolledOverPercent,
  checkWebPSupport,
  addQuery,
} from '../utils'

const Root = styled.div`
  position: relative;
  min-height: ${props => props.minHeight};
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: ${props => (props.isCentered ? 'center' : 'flex-start')};
  align-items: ${props => (props.isCentered ? 'center' : 'stretch')};
  text-align: ${props => (props.isCentered ? 'center' : 'left')};
`

const Cover = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`
const Img = styled(Cover)`
  background-attachment: ${props => (props.isFixed ? 'fixed' : 'scroll')};
  background-image: url(${props => props.src});
  background-position: center;
  background-repeat: no-repeat;
  background-size: ${props =>
    props.width ? `${props.width}px ${props.height}px` : 'cover'};
  /* background-size: cover; */
  opacity: ${props => (props.isVisible ? 1 : 0)};
  transition-duration: ${props => `${props.transitionDuration}ms`};
  transition-property: opacity;
  transition-timing-function: ${props => props.transitionTimingFunction};
`

const Overlay = styled(Cover)`
  min-height: 100%;
  display: flex;
  justify-content: ${props => (props.isCentered ? 'center' : 'flex-start')};
  align-items: ${props => (props.isCentered ? 'center' : 'stretch')};
  text-align: ${props => (props.isCentered ? 'center' : 'left')};
  background-color: ${props => transparentize(props.opacity, props.color)};
`

class Hero extends Component {
  constructor(props) {
    super(props)
    this.state = {
      backgroundPositionY: 'center',
      backgroundDimensions: null,
      heroDimensions: null,
      image: null,
      isInViewport: false,
    }
    this.handleResize = this.handleResize.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.updatePosition = this.updatePosition.bind(this)
    this.updateSize = this.updateSize.bind(this)
  }

  componentDidMount() {
    this.updateBestSize()

    if (this.props.parallaxOffset > 0) {
      window.addEventListener('scroll', this.handleScroll)
      window.addEventListener('resize', this.handleResize)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.imageSrc !== prevProps.imageSrc ||
      this.state.bestSize !== prevState.bestSize
    ) {
      this.loadImage()
    }
  }

  componentWillUnmount() {
    if (this.props.parallaxOffset > 0) {
      window.removeEventListener('scroll', this.handleScroll)
      window.removeEventListener('resize', this.handleResize)
    }
  }

  handleScroll() {
    this.updatePosition()
  }

  handleResize() {
    this.updateBestSize()
    this.updateSize()
    this.updatePosition()
  }

  updateBestSize = () => {
    const newBestSize = [
      200,
      400,
      600,
      900,
      1200,
      1600,
      2000,
      2500,
      3000,
      3600,
      4000,
    ].find(size => size >= this.ref.offsetWidth)

    const bestSize =
      this.state.bestSize && this.state.bestSize >= newBestSize
        ? this.state.bestSize
        : newBestSize

    this.setState({ bestSize })
  }

  loadImage() {
    const image = new Image()
    checkWebPSupport('lossless', (feature, support) => {
      const format = support ? 'webp' : 'png'
      image.src = addQuery(this.props.imageSrc, {
        w: this.state.bestSize,
        fit: 'min',
        fm: format,
      })
      image.onload = () => {
        this.setState({ image })

        if (this.props.parallaxOffset > 0) {
          this.updateSize()
          this.updatePosition()
        }
      }
    })
  }

  updateSize() {
    if (!this.state.image) return

    const heroDimensions = {
      height: this.ref.offsetHeight,
      width: this.ref.offsetWidth,
    }

    const imageDimensions = {
      height: this.state.image.height,
      width: this.state.image.width,
    }

    const resizedImage = resizeToCover(imageDimensions, heroDimensions)
    const initialVisibleImageHeight =
      resizedImage.height - this.props.parallaxOffset

    const minHeight =
      initialVisibleImageHeight < heroDimensions.height
        ? resizedImage.height +
          heroDimensions.height -
          initialVisibleImageHeight
        : resizedImage.height

    const finalHeight = minHeight + this.ref.offsetTop * 2

    const backgroundDimensions = resizeToCover(imageDimensions, {
      height: finalHeight,
    })
    this.setState({ backgroundDimensions, heroDimensions })
  }

  updatePosition() {
    if (!this.state.backgroundDimensions) return
    const position =
      0 +
      this.ref.offsetTop -
      // Center image vertically
      this.state.backgroundDimensions.height / 2 +
      this.state.heroDimensions.height / 2 -
      this.props.parallaxOffset / 2 +
      // Apply scroll position
      this.props.parallaxOffset * scrolledOverPercent(this.ref)

    this.setState({ backgroundPositionY: `${Math.round(position)}px` })
  }

  render() {
    const { backgroundDimensions, backgroundPositionY } = this.state
    return (
      <Root
        className={this.props.className}
        ref={r => {
          this.ref = r
        }}
        isCentered={this.props.isCentered}
        minHeight={this.props.minHeight}
        style={this.props.style}
      >
        <Img
          height={backgroundDimensions && backgroundDimensions.height}
          isFixed={this.props.isFixed || this.props.parallaxOffset > 0}
          src={this.state.image && this.state.image.src}
          isVisible={!!this.state.image}
          style={{ backgroundPositionY }}
          transitionDuration={this.props.transitionDuration}
          transitionTimingFunction={this.props.transitionTimingFunction}
          width={backgroundDimensions && backgroundDimensions.width}
        />
        <Img
          height={backgroundDimensions && backgroundDimensions.height}
          isFixed={this.props.isFixed || this.props.parallaxOffset > 0}
          src={this.props.imagePlaceholder}
          isVisible={!this.state.image}
          style={{ backgroundPositionY }}
          transitionDuration={this.props.transitionDuration}
          transitionTimingFunction={this.props.transitionTimingFunction}
          width={backgroundDimensions && backgroundDimensions.width}
        />
        <Overlay
          color={this.props.color}
          isCentered={this.props.isCentered}
          opacity={this.props.opacity}
        />
        <div style={{ transform: 'translateZ(0)', width: '100%' }}>
          {this.props.children}
        </div>
      </Root>
    )
  }
}

Hero.defaultProps = {
  children: undefined,
  className: undefined,
  color: '#fff',
  imageSrc: undefined,
  isCentered: true,
  isFixed: false,
  minHeight: '33vh',
  opacity: 0.2,
  parallaxOffset: 0,
  style: undefined,
  transitionDuration: 600,
  transitionTimingFunction: 'ease-in-out',
}

Hero.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.string,
  imageSrc: PropTypes.string,
  isCentered: PropTypes.bool,
  isFixed: PropTypes.bool,
  minHeight: PropTypes.string,
  opacity: PropTypes.number,
  parallaxOffset: PropTypes.number,
  style: PropTypes.object,
  transitionDuration: PropTypes.number,
  transitionTimingFunction: PropTypes.string,
}

export default Hero



// WEBPACK FOOTER //
// /opt/build/repo/src/components/Hero.jsx